import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { displayRole } from "@/lib/roleDisplay";
import { Card, PageHeader, Button, Field, Input, Select, Modal, Badge } from "@/components/ui-kit";
import { Plus, Pencil, Trash2, Eye, Shield, Power } from "lucide-react";

// Iter v34m (#1) — Permission groups with human-readable Bahasa Indonesia labels
// matching the menu/feature each permission controls.
const PERM_GROUPS = [
  {
    name: "Dashboard",
    perms: [
      { key: "dashboard.view", label: "Lihat Dashboard utama" },
      { key: "dashboard.finance", label: "Lihat kartu Finance / Tagihan" },
      { key: "dashboard.po", label: "Lihat kartu PO" },
      { key: "dashboard.sj", label: "Lihat kartu Surat Jalan" },
      { key: "dashboard.notifications", label: "Lihat panel notifikasi" },
    ],
  },
  {
    name: "Daftar Harga (Pricelist)",
    perms: [
      { key: "price.view", label: "Lihat Daftar Harga" },
      { key: "price.create", label: "Tambah harga baru" },
      { key: "price.edit", label: "Edit harga" },
      { key: "price.delete", label: "Hapus harga" },
      { key: "price.print", label: "Cetak / export Daftar Harga" },
    ],
  },
  {
    name: "Penawaran (Quotation)",
    perms: [
      { key: "quotation.view", label: "Lihat Penawaran" },
      { key: "quotation.create", label: "Buat Penawaran baru" },
      { key: "quotation.edit", label: "Edit Penawaran" },
      { key: "quotation.delete", label: "Hapus Penawaran" },
    ],
  },
  {
    name: "Purchase Order (PO)",
    perms: [
      { key: "po.view", label: "Lihat PO" },
      { key: "po.create", label: "Buat PO baru" },
      { key: "po.edit", label: "Edit PO" },
      { key: "po.delete", label: "Hapus PO" },
      { key: "po.print", label: "Cetak PDF PO" },
    ],
  },
  {
    name: "Surat Jalan (SJ)",
    perms: [
      { key: "sj.view", label: "Lihat Surat Jalan" },
      { key: "sj.create", label: "Buat Surat Jalan baru" },
      { key: "sj.edit", label: "Edit Surat Jalan" },
      { key: "sj.delete", label: "Hapus Surat Jalan" },
      { key: "sj.print", label: "Cetak PDF Surat Jalan" },
      { key: "sj.recurring_billing", label: "Izin SJ ditagih berkali-kali" },
    ],
  },
  {
    name: "Tagihan & Pembayaran (Invoice)",
    perms: [
      { key: "invoice.view", label: "Lihat Tagihan" },
      { key: "invoice.create", label: "Buat Tagihan baru (Billing Wizard)" },
      { key: "invoice.edit", label: "Edit Tagihan + catat pembayaran" },
      { key: "invoice.delete", label: "Hapus Tagihan" },
      { key: "invoice.print", label: "Cetak PDF Tagihan" },
    ],
  },
  {
    name: "Stok Barang",
    perms: [
      { key: "stock.view", label: "Lihat Stok" },
      { key: "stock.create", label: "Tambah item Stok baru" },
      { key: "stock.edit", label: "Edit info Stok (nama, satuan, harga)" },
      { key: "stock.stockin", label: "Catat Barang Masuk (Stock In)" },
      { key: "stock.delete", label: "Hapus item Stok" },
      { key: "stocklog.view", label: "Lihat log pergerakan Stok" },
    ],
  },
  {
    name: "Material Rusak / Hilang",
    perms: [
      { key: "damage.view", label: "Lihat Laporan Material Rusak" },
      { key: "damage.create", label: "Buat Laporan Material Rusak / Hilang" },
      { key: "damage.delete", label: "Hapus Laporan Material Rusak" },
    ],
  },
  {
    name: "Pengeluaran (Expense)",
    perms: [
      { key: "expense.view", label: "Lihat Pengeluaran" },
      { key: "expense.create", label: "Catat Pengeluaran baru" },
      { key: "expense.edit", label: "Edit Pengeluaran" },
      { key: "expense.delete", label: "Hapus Pengeluaran" },
      { key: "expense.print", label: "Export / Cetak Pengeluaran" },
    ],
  },
  {
    name: "HR — Karyawan & Absensi",
    perms: [
      { key: "employees.view", label: "Lihat data Karyawan" },
      { key: "employees.edit", label: "Edit Karyawan + kelola hak Hutang / Kuota Cuti" },
      { key: "employees.import", label: "Import Karyawan dari Excel" },
      { key: "attendance.view", label: "Lihat Rekap Absensi (admin/HR)" },
      { key: "attendance.review", label: "Setujui / tolak absensi pending" },
      { key: "attendance.kiosk", label: "Akses halaman Absen Kiosk (Face-Scan bersama)" },
      { key: "attendance.edit", label: "Edit waktu absensi (clock-in/out) di rekap" },
      { key: "attendance.delete", label: "Hapus record absensi di rekap" },
      { key: "attendance.clock", label: "Bisa clock-in / clock-out (untuk karyawan)" },
    ],
  },
  {
    name: "Klien (Portal Klien)",
    perms: [
      { key: "client.dashboard", label: "Akses Dashboard Klien" },
      { key: "client.catalog", label: "Akses Katalog (klien)" },
    ],
  },
  {
    name: "Lainnya",
    perms: [
      { key: "chat.use", label: "Akses fitur Chat (operator/CS)" },
      { key: "report.view", label: "Lihat halaman Laporan" },
      { key: "settings.manage", label: "Kelola Pengaturan & Master Data" },
    ],
  },
];

const ALL_PERMS = PERM_GROUPS.flatMap((g) => g.perms.map((p) => p.key));
const PERM_LABEL_MAP = Object.fromEntries(
  PERM_GROUPS.flatMap((g) => g.perms.map((p) => [p.key, p.label])),
);

const DEFAULT_PERM_GROUPS = PERM_GROUPS;

export default function Users() {
  const { t } = useLang();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [clients, setClients] = useState([]);
  // Iter v34n (#6) — Permissions catalog from backend (single source-of-truth).
  // Fallback to hardcoded list above for instant first render.
  const [permGroups, setPermGroups] = useState(DEFAULT_PERM_GROUPS);

  // user modal
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "operator", password: "", permissions: [], account_type: "non_klien", client_id: "", department_scopes: [] });
  const [accountFilter, setAccountFilter] = useState("all");
  // Iter v34p — list of dept categories for the multi-select scope picker.
  const [deptCats, setDeptCats] = useState([]);

  // role modal
  const [openRole, setOpenRole] = useState(false);
  const [editRole, setEditRole] = useState(null);
  const [roleForm, setRoleForm] = useState({ name: "", label: "", permissions: [] });

  // view password modal
  const [pwInfo, setPwInfo] = useState(null);

  const load = async () => {
    const [u, r, c] = await Promise.all([api.get("/users"), api.get("/roles"), api.get("/clients")]);
    setUsers(u.data); setRoles(r.data); setClients(c.data);
    // Refresh perm catalog from backend so newly-registered perms appear instantly.
    try {
      const cat = await api.get("/permissions/catalog");
      if (cat?.data?.groups?.length) setPermGroups(cat.data.groups);
    } catch (_e) { /* keep hardcoded fallback */ }
    // Iter v34p — load department categories for the scope multi-select
    try {
      const d = await api.get("/department-categories");
      setDeptCats((d?.data || []).map((x) => (typeof x === "string" ? x : x?.name)).filter(Boolean));
    } catch (_e) { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      if (form.account_type === "klien" && !form.client_id) {
        toast.error("Pilih Klien untuk akun bertipe Akun Klien");
        return;
      }
      if (edit) {
        const payload = {};
        if (form.name) payload.name = form.name;
        if (form.role) payload.role = form.role;
        if (form.permissions) payload.permissions = form.permissions;
        if (form.password) payload.password = form.password;
        payload.account_type = form.account_type;
        if (form.account_type === "klien") payload.client_id = form.client_id;
        payload.department_scopes = form.department_scopes || [];
        await api.patch(`/users/${edit.id}`, payload);
        toast.success("User tersimpan. Catatan: user perlu login ulang agar perubahan role/permission berlaku.");
      } else {
        const payload = { ...form };
        if (payload.account_type !== "klien") delete payload.client_id;
        await api.post("/users", payload);
        toast.success("Tersimpan");
      }
      setOpen(false); setEdit(null); setForm({ email: "", name: "", role: "operator", password: "", permissions: [], account_type: "non_klien", client_id: "", department_scopes: [] }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const submitRole = async () => {
    try {
      if (editRole) {
        await api.patch(`/roles/${editRole.id}`, roleForm);
        toast.success("Role tersimpan. Catatan: user dengan role ini perlu login ulang agar perubahan berlaku.");
      } else {
        await api.post("/roles", roleForm);
        toast.success("Role tersimpan");
      }
      setOpenRole(false); setEditRole(null); setRoleForm({ name: "", label: "", permissions: [] }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  const del = async (id) => { if (!window.confirm("Hapus user?")) return; await api.delete(`/users/${id}`); load(); };
  // Iter v34ca (#6) — Toggle akun aktif/nonaktif
  const toggleActive = async (u) => {
    const currActive = u.is_active !== false;
    const action = currActive ? "menonaktifkan" : "mengaktifkan kembali";
    if (!window.confirm(`Yakin ingin ${action} akun ${u.email}?`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/toggle-active`);
      toast.success(`Akun ${data.is_active ? "diaktifkan" : "dinonaktifkan"}.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal toggle akun"); }
  };
  const delRole = async (id) => { if (!window.confirm("Hapus role?")) return; await api.delete(`/roles/${id}`); load(); };

  const viewPw = async (u) => {
    try {
      const r = await api.get(`/users/${u.id}/password`);
      setPwInfo(r.data);
    } catch (e) { toast.error("Gagal"); }
  };

  const toggleRolePerm = (p) => {
    setRoleForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));
  };
  const toggleUserPerm = (p) => {
    setForm((f) => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter((x) => x !== p) : [...f.permissions, p] }));
  };

  return (
    <div className="min-h-full bg-[#f6f5f0]">
    <div className="max-w-[1600px] mx-auto p-4 md:p-6 lg:p-8 space-y-8">
      <PageHeader
        overline="Admin · Super Admin"
        title={t("users")}
        description="Kelola user, role kustom, dan hak akses fitur (Edit/Print/Delete) untuk membatasi modul tertentu."
        action={
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => { setEditRole(null); setRoleForm({ name: "", label: "", permissions: [] }); setOpenRole(true); }} data-testid="add-role-btn"><Shield className="h-4 w-4" />{t("new_role")}</Button>
            <Button onClick={() => { setEdit(null); setForm({ email: "", name: "", role: "operator", password: "", permissions: [], account_type: "non_klien", client_id: "", department_scopes: [] }); setOpen(true); }} data-testid="add-user-btn"><Plus className="h-4 w-4" />{t("create_user")}</Button>
          </div>
        }
      />

      {/* Bento KPI + role distribution */}
      {(() => {
        const nonKlien = users.filter((u) => (u.account_type || "non_klien") === "non_klien").length;
        const klien = users.filter((u) => u.account_type === "klien").length;
        const karyawan = users.filter((u) => u.account_type === "karyawan").length;
        const superAdmin = users.filter((u) => u.role === "super_admin").length;
        const total = users.length;
        const bars = [
          { label: "Non-Klien", val: nonKlien, color: "from-blue-500 to-emerald-400" },
          { label: "Karyawan", val: karyawan, color: "from-emerald-500 to-teal-400" },
          { label: "Klien", val: klien, color: "from-amber-500 to-orange-400" },
        ];
        return (
          <div className="grid grid-cols-12 gap-3">
            <div className="col-span-6 md:col-span-3 rounded-2xl bg-white border border-stone-200/70 p-4">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">Total User</div>
              <div className="font-display text-3xl mt-1 text-stone-900">{total}</div>
            </div>
            <div className="col-span-6 md:col-span-3 rounded-2xl bg-gradient-to-br from-stone-900 to-blue-950 text-white p-4">
              <div className="text-[10px] uppercase tracking-wider text-blue-200 font-semibold">Super Admin</div>
              <div className="font-display text-3xl mt-1">{superAdmin}</div>
            </div>
            <div className="col-span-12 md:col-span-6 rounded-2xl bg-white border border-stone-200/70 p-4">
              <div className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold mb-2">Distribusi per Tipe</div>
              <div className="space-y-1.5">
                {bars.map((b) => {
                  const pct = total > 0 ? (b.val / total) * 100 : 0;
                  return (
                    <div key={b.label}>
                      <div className="flex items-center justify-between text-[11px] mb-0.5">
                        <span className="text-stone-700">{b.label}</span>
                        <span className="font-mono-num text-stone-700">{b.val} <span className="text-stone-400">({pct.toFixed(0)}%)</span></span>
                      </div>
                      <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                        <div className={`h-full bg-gradient-to-r ${b.color} rounded-full`} style={{ width: pct + "%" }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Users — Iter v33: tab filter by account_type */}
      <Card className="overflow-x-auto">
        <div className="px-5 py-3 border-b border-stone-200 bg-stone-50 flex flex-wrap items-center justify-between gap-2">
          <p className="text-[10px] uppercase tracking-widest font-semibold text-stone-600">Users</p>
          <div className="flex border border-stone-300" data-testid="users-account-filter">
            {[
              { k: "all", l: `Semua (${users.length})` },
              { k: "non_klien", l: `Non-Klien (${users.filter((u) => (u.account_type || "non_klien") === "non_klien").length})` },
              { k: "klien", l: `Klien (${users.filter((u) => u.account_type === "klien").length})` },
              { k: "karyawan", l: `Pekerja (${users.filter((u) => u.account_type === "karyawan").length})` },
            ].map((t) => (
              <button key={t.k} onClick={() => setAccountFilter(t.k)}
                className={`px-3 py-1 text-xs uppercase tracking-wider ${accountFilter === t.k ? "bg-terra-500 text-white" : "bg-white text-stone-700 hover:bg-stone-50"}`}
                data-testid={`users-filter-${t.k}`}>{t.l}</button>
            ))}
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
              <th className="py-3 px-3">{t("name")}</th>
              <th className="py-3 px-3">{t("email")}</th>
              <th className="py-3 px-3">{t("role")}</th>
              <th className="py-3 px-3">Tipe / Klien</th>
              <th className="py-3 px-3">{t("permissions")}</th>
              <th className="py-3 px-3 text-right">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {users.filter((u) => accountFilter === "all" || (u.account_type || "non_klien") === accountFilter).map((u, idx) => (
              <tr key={u.id} className="border-b border-stone-100 hover:bg-stone-50/50" data-testid={`user-row-${idx}`}>
                <td className="py-2.5 px-3 font-medium">{u.name}</td>
                <td className="py-2.5 px-3 font-mono">{u.email}</td>
                <td className="py-2.5 px-3"><Badge tone={u.role === "super_admin" ? "danger" : "stone"}>{displayRole(u.role)}</Badge></td>
                <td className="py-2.5 px-3 text-xs">
                  {(u.account_type === "klien") ? (
                    <span><Badge tone="terra">KLIEN</Badge> <span className="text-stone-700 ml-1">{u.client_name || "—"}</span></span>
                  ) : (
                    <div className="flex flex-col gap-1">
                      <span className="text-stone-400">Non Klien</span>
                      {(u.department_scopes || []).length > 0 && (
                        <div className="flex flex-wrap gap-1" data-testid={`user-dept-scopes-display-${idx}`}>
                          {(u.department_scopes || []).map((d) => (
                            <span key={d} className="bg-terra-100 text-terra-800 px-1.5 py-0.5 text-[10px] font-medium">{d}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </td>
                <td className="py-2.5 px-3 text-stone-500 text-xs">{(u.permissions || []).join(", ") || "—"}</td>
                <td className="py-2.5 px-3 text-right">
                  <button onClick={() => viewPw(u)} className="text-stone-500 hover:text-blue-700 px-2" data-testid={`view-pw-${idx}`}><Eye className="h-3.5 w-3.5" /></button>
                  {u.role !== "super_admin" && (
                    <button onClick={() => toggleActive(u)} className={`px-2 ${u.is_active === false ? "text-red-500 hover:text-emerald-700" : "text-stone-500 hover:text-red-700"}`} title={u.is_active === false ? "Aktifkan akun" : "Nonaktifkan akun"} data-testid={`toggle-active-${idx}`}>
                      <Power className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button onClick={() => { setEdit(u); setForm({ email: u.email, name: u.name, role: u.role, password: "", permissions: u.permissions || [], account_type: u.account_type || "non_klien", client_id: u.client_id || "", department_scopes: u.department_scopes || [] }); setOpen(true); }} className="text-stone-500 hover:text-terra-600 px-2"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={() => del(u.id)} className="text-stone-500 hover:text-red-700 px-2"><Trash2 className="h-3.5 w-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Roles */}
      <Card className="overflow-x-auto">
        <div className="px-5 py-3 border-b border-stone-200 bg-stone-50">
          <p className="text-[10px] uppercase tracking-widest font-semibold text-stone-600">Roles</p>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-white border-b border-stone-200 text-left text-[10px] font-semibold uppercase tracking-wider text-stone-600">
              <th className="py-3 px-3">{t("name")}</th>
              <th className="py-3 px-3">Label</th>
              <th className="py-3 px-3">{t("permissions")}</th>
              <th className="py-3 px-3 text-right">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((r, idx) => (
              <tr key={r.id} className="border-b border-stone-100" data-testid={`role-row-${idx}`}>
                <td className="py-2.5 px-3 font-mono">{r.name === "super_admin" ? "unknown" : r.name}</td>
                <td className="py-2.5 px-3 font-medium">{r.name === "super_admin" ? "Unknown" : r.label}</td>
                <td className="py-2.5 px-3 text-stone-500 text-xs">{(r.permissions || []).slice(0, 8).join(", ")}{(r.permissions || []).length > 8 ? "…" : ""}</td>
                <td className="py-2.5 px-3 text-right">
                  <button onClick={() => { setEditRole(r); setRoleForm({ name: r.name, label: r.label, permissions: r.permissions || [] }); setOpenRole(true); }} className="text-stone-500 hover:text-terra-600 px-2"><Pencil className="h-3.5 w-3.5" /></button>
                  {r.name !== "super_admin" && <button onClick={() => delRole(r.id)} className="text-stone-500 hover:text-red-700 px-2"><Trash2 className="h-3.5 w-3.5" /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* User modal */}
      <Modal open={open} onClose={() => setOpen(false)} title={edit ? t("edit") + " " + t("users") : t("create_user")} size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t("email")} required><Input type="email" disabled={!!edit} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></Field>
            <Field label={t("name")} required><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name" /></Field>
            <Field label={t("password") + (edit ? " (kosongkan jika tidak diubah)" : "")} required={!edit}><Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></Field>
            <Field label={t("role")}>
              <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role">
                {roles.map((r) => <option key={r.id} value={r.name}>{r.label}</option>)}
              </Select>
            </Field>
            <Field label="Tipe Akun" hint="Akun Klien hanya akses Beranda, Daftar Harga, dan Chat CS">
              <Select value={form.account_type} onChange={(e) => setForm({ ...form, account_type: e.target.value, client_id: e.target.value === "non_klien" ? "" : form.client_id })} data-testid="user-account-type">
                <option value="non_klien">Non Klien (akun internal biasa)</option>
                <option value="klien">Akun Klien</option>
              </Select>
            </Field>
            {form.account_type === "klien" && (
              <Field label="Pilih Klien" required hint="Tersinkron dengan Pengaturan Database → Klien">
                <Select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })} data-testid="user-client-id">
                  <option value="">— pilih klien —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </Select>
              </Field>
            )}
          </div>

          {/* Iter v34p (#1+#2) — Department-scope multi-select. Empty = akses semua dept. */}
          {form.account_type !== "klien" && (
            <Field
              label="Departemen yang Ditangani"
              hint="Kosongkan untuk akses semua departemen. Jika diisi, user hanya bisa melihat/membalas data karyawan dari departemen ini (berlaku di Rekap Absensi, Persetujuan Cuti, Audit Wajah, Inbox Chat HR). Super Admin tetap akses semua."
            >
              <div className="border border-stone-200 bg-stone-50 p-2 max-h-40 overflow-y-auto">
                {deptCats.length === 0 && (
                  <div className="text-[11px] text-stone-400 italic px-2 py-1">Belum ada Departemen di Master Data → Kategori Departemen.</div>
                )}
                <div className="flex flex-wrap gap-1.5">
                  {deptCats.map((d) => {
                    const checked = (form.department_scopes || []).includes(d);
                    return (
                      <label
                        key={d}
                        className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs cursor-pointer border ${checked ? "bg-terra-100 border-terra-400 text-terra-900" : "bg-white border-stone-300 hover:border-stone-400"}`}
                        data-testid={`user-dept-scope-${d}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const next = checked
                              ? form.department_scopes.filter((x) => x !== d)
                              : [...(form.department_scopes || []), d];
                            setForm({ ...form, department_scopes: next });
                          }}
                        />
                        <span>{d}</span>
                      </label>
                    );
                  })}
                </div>
                {(form.department_scopes || []).length > 0 && (
                  <div className="flex justify-end mt-2">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, department_scopes: [] })}
                      className="text-[10px] uppercase tracking-wider text-stone-500 hover:text-red-700"
                      data-testid="user-dept-scope-clear"
                    >
                      Bersihkan ({form.department_scopes.length} dipilih → akses semua)
                    </button>
                  </div>
                )}
              </div>
            </Field>
          )}

          <Field label={t("permissions") + " (override khusus user)"}>
            <PermissionGrid
              groups={permGroups}
              selected={form.permissions}
              onChange={(perms) => setForm({ ...form, permissions: perms })}
              testidPrefix="user-perm"
            />
          </Field>

          <div className="flex justify-end gap-2 pt-4 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpen(false)}>{t("cancel")}</Button>
            <Button onClick={submit} data-testid="save-user-btn">{t("save")}</Button>
          </div>
        </div>
      </Modal>

      {/* Role modal */}
      <Modal open={openRole} onClose={() => setOpenRole(false)} title={editRole ? t("edit") + " Role" : t("new_role")} size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Slug (a-z, _)" required><Input value={roleForm.name} onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} data-testid="role-name" /></Field>
            <Field label="Label" required><Input value={roleForm.label} onChange={(e) => setRoleForm({ ...roleForm, label: e.target.value })} data-testid="role-label" /></Field>
          </div>
          <Field label={t("permissions")}>
            <PermissionGrid
              groups={permGroups}
              selected={roleForm.permissions}
              onChange={(perms) => setRoleForm({ ...roleForm, permissions: perms })}
              testidPrefix="role-perm"
            />
          </Field>
          <div className="flex justify-end gap-2 pt-4 border-t border-stone-200">
            <Button variant="ghost" onClick={() => setOpenRole(false)}>{t("cancel")}</Button>
            <Button onClick={submitRole} data-testid="save-role-btn">{t("save")}</Button>
          </div>
        </div>
      </Modal>

      {/* View password modal */}
      <Modal open={!!pwInfo} onClose={() => setPwInfo(null)} title="Password User">
        {pwInfo && (
          <div className="space-y-3">
            <p className="text-xs text-stone-500">Hanya dapat dilihat oleh Super Admin. Tindakan ini tercatat di log sistem.</p>
            <div className="bg-stone-50 border border-stone-200 p-4">
              <p className="text-xs uppercase tracking-widest text-stone-500 mb-1">Email</p>
              <p className="font-mono text-sm">{pwInfo.email}</p>
              <p className="text-xs uppercase tracking-widest text-stone-500 mb-1 mt-3">Password</p>
              <p className="font-mono text-lg text-stone-900" data-testid="pw-display">{pwInfo.password_plain}</p>
            </div>
          </div>
        )}
      </Modal>
    </div>
    </div>
  );
}

/**
 * PermissionGrid — grouped checkbox grid with Select All / Clear All header.
 * Each permission shows a human-readable Bahasa Indonesia label (Iter v34m #1).
 */
function PermissionGrid({ groups, selected, onChange, testidPrefix }) {
  const groupsToUse = groups || DEFAULT_PERM_GROUPS;
  const allPerms = groupsToUse.flatMap((g) => g.perms.map((p) => p.key));
  const allSelected = selected.length === allPerms.length;
  const someSelected = selected.length > 0 && !allSelected;
  const toggleOne = (perm) => {
    onChange(selected.includes(perm) ? selected.filter((x) => x !== perm) : [...selected, perm]);
  };
  const selectAll = () => onChange([...allPerms]);
  const clearAll = () => onChange([]);
  const toggleGroup = (group) => {
    const keys = group.perms.map((p) => p.key);
    const allOn = keys.every((k) => selected.includes(k));
    if (allOn) {
      onChange(selected.filter((k) => !keys.includes(k)));
    } else {
      onChange([...new Set([...selected, ...keys])]);
    }
  };
  return (
    <div className="border border-stone-200 bg-stone-50">
      <div className="flex items-center justify-between px-3 py-2 border-b border-stone-200 bg-white sticky top-0 z-10">
        <label className="flex items-center gap-2 text-xs font-semibold text-stone-700 cursor-pointer">
          <input
            type="checkbox"
            checked={allSelected}
            ref={(el) => { if (el) el.indeterminate = someSelected; }}
            onChange={(e) => (e.target.checked ? selectAll() : clearAll())}
            data-testid={`${testidPrefix}-select-all`}
          />
          Pilih Semua ({selected.length} / {allPerms.length})
        </label>
        <div className="flex gap-2">
          <button type="button" onClick={selectAll} className="text-[10px] uppercase tracking-wider text-terra-700 hover:text-terra-900" data-testid={`${testidPrefix}-all`}>Semua</button>
          <span className="text-stone-300">·</span>
          <button type="button" onClick={clearAll} className="text-[10px] uppercase tracking-wider text-stone-500 hover:text-red-700" data-testid={`${testidPrefix}-clear`}>Bersihkan</button>
        </div>
      </div>
      <div className="max-h-96 overflow-y-auto">
        {groupsToUse.map((group) => {
          const keys = group.perms.map((p) => p.key);
          const groupSelected = keys.filter((k) => selected.includes(k)).length;
          const groupAll = groupSelected === keys.length;
          const groupSome = groupSelected > 0 && !groupAll;
          return (
            <div key={group.name} className="border-b border-stone-200 last:border-b-0">
              <label className="flex items-center gap-2 px-3 py-2 bg-stone-100/80 hover:bg-stone-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={groupAll}
                  ref={(el) => { if (el) el.indeterminate = groupSome; }}
                  onChange={() => toggleGroup(group)}
                  data-testid={`${testidPrefix}-group-${group.name.replace(/[^a-z]/gi, "").toLowerCase()}`}
                />
                <span className="text-xs font-semibold text-stone-800 uppercase tracking-wider">{group.name}</span>
                <span className="text-[10px] text-stone-500 ml-auto">{groupSelected} / {keys.length}</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1 p-2 pl-6 bg-white">
                {group.perms.map((p) => (
                  <label key={p.key} className="flex items-start gap-2 text-xs cursor-pointer hover:bg-stone-50/80 px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={selected.includes(p.key)}
                      onChange={() => toggleOne(p.key)}
                      data-testid={`${testidPrefix}-${p.key}`}
                      className="mt-0.5"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-stone-800">{p.label}</div>
                      <div className="text-[10px] text-stone-400 font-mono">{p.key}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
